import { useEffect, useState, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Eye, MessageSquare, CalendarClock, Heart, Gift, ChevronRight, Clock, Share2, Tag, ArrowRight, BadgePercent } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../../../components/ui/button';
import { Switch } from '../../../components/ui/switch';
import { Tabs, TabsList, TabsTrigger, TabsContent, TabsCount } from '../../../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../../components/ui/dialog';
import { vehicleActivityAPI, vehicleLeadsAPI, rebateAlertsAPI, fiLeadsAPI, vehicleSettingsAPI } from '../../../lib/api';
import { AutoSalesAlerts } from '../AutoSalesAlerts';
import { RebateAlerts } from '../RebateAlerts';
import NotifyRoutingPanel from '../NotifyRoutingPanel';
import VehicleNotificationsPanel from '../VehicleNotificationsPanel';
import CCBuyerExperiencePanel from '../CCBuyerExperiencePanel';
import CCFILeadsPanel from '../CCFILeadsPanel';
import CCSpecialSchedules from '../CCSpecialSchedules';
import { CollapsibleList } from '../CollapsibleList';

const EVENT_LABELS = {
    'vehicle.tap': 'Tap Visit', 'vehicle.contact_sales': 'Question', 'vehicle.sms_out': 'SMS → customer',
    'vehicle.sms_in': 'SMS ← customer', 'vehicle.test_drive_requested': 'Test drive requested',
    'vehicle.test_drive_booked': 'Test drive booked', 'vehicle.test_drive_declined': 'Test drive declined',
    'vehicle.saved': 'Saved', 'vehicle.special_offer_click': 'Offer claimed',
    // F&I desk (Phase B) + the CONFIDENTIAL insurance signal (Phase C). The
    // insurance row is a SIGNAL only — the thread itself is never reachable here.
    'vehicle.fi_calculator_opened': 'Payment calculator opened',
    'vehicle.fi_prequal_submitted': 'Financing application',
    'vehicle.fi_warranty_interest': 'Warranty interest',
    'vehicle.insurance_quote_requested': 'Insurance quote requested (confidential)',
    'vehicle.trade_in_submitted': 'Trade-in submitted',
    'vehicle.form_abandoned': 'Started a form, didn’t finish',
    'vehicle.test_drive_gift_issued': 'Test drive gift sent',
    'vehicle.review_requested': 'Review request sent',
    'vehicle.share_created': 'Vehicle Shared', 'vehicle.share_opened': 'Shared Link Opened',
    'vehicle.share_viewed': 'Shared Vehicle Viewed',
};
const fmt = (iso) => { try { return new Date(iso).toLocaleString(); } catch { return iso; } };
const TABS = ['rebate', 'activity', 'alerts', 'notify', 'fileads', 'buyer'];
const TAB_KEY = 'yulontap_cc_auto_tab';

/**
 * Automotive KPI funnel — vertical panel for the SHARED Command Center shell.
 * Self-contained: fetches its own data via the EXISTING vehicle-activity /
 * vehicle-leads APIs (no backend change). Renders the funnel (Visits /
 * Questions / Test drives / Saves / Offers) plus the Live (needs-attention) and
 * Activity streams. Extracted verbatim from the former AutomotiveCommandCenter.
 */
export default function AutomotiveFunnelPanel({ organizationId, locationIds, focusDept }) {
    const { t } = useTranslation();
    const [days, setDays] = useState(7);
    const [sum, setSum] = useState(null);
    const [drives, setDrives] = useState([]);
    const [sales, setSales] = useState([]);
    const [openFILeads, setOpenFILeads] = useState(0);
    const [fiApps, setFiApps] = useState([]);
    const [fiInterest, setFiInterest] = useState([]);
    const [fiFailed, setFiFailed] = useState(false);
    const [stream, setStream] = useState([]);
    const [convo, setConvo] = useState(null);
    const [rebateCount, setRebateCount] = useState(0);
    // Org-wide payment-estimate default. Lives HERE, in the always-visible zone
    // of the dashboard, because it governs every vehicle page — a manager should
    // not have to open a vehicle to find it. Same field + endpoint as the
    // Showroom Settings dialog; no second flag, no duplicated state.
    const [payEst, setPayEst] = useState(null);
    const [payEstBusy, setPayEstBusy] = useState(false);
    // Scrolling the tab content into view is what makes the promotion banner
    // feel like it did something — the tab switch alone happens below the fold.
    const tabsRef = useRef(null);
    // The manager's last tab survives a reload. Validated against TABS on read,
    // so a tab that is later removed (as "live" was) can never strand the panel
    // on a value with no content — it falls back to the sales floor's queue.
    const [tab, setTab] = useState(() => {
        try {
            const saved = localStorage.getItem(TAB_KEY);
            return TABS.includes(saved) ? saved : 'alerts';
        } catch { return 'alerts'; }
    });
    const pickTab = useCallback((v) => {
        setTab(v);
        try { localStorage.setItem(TAB_KEY, v); } catch { /* private mode */ }
    }, []);

    /** Tab select + bring the content into view. Stays a SELECT, never a
     *  toggle: none of the other five tabs can be deselected to blank, and
     *  this one must not be the exception. */
    const pickTabAndReveal = useCallback((v) => {
        pickTab(v);
        // One frame, so the newly selected TabsContent exists before we scroll.
        requestAnimationFrame(() => {
            tabsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        });
    }, [pickTab]);

    const load = useCallback(async () => {
        if (!organizationId) return;
        const [s, td, sr, st] = await Promise.all([
            vehicleActivityAPI.summary(organizationId, days),
            vehicleLeadsAPI.testDrives(organizationId),
            vehicleLeadsAPI.salesRequests(organizationId),
            vehicleActivityAPI.stream(organizationId, { limit: 100 }),
        ]);
        setSum(s.data); setDrives(td.data || []); setSales(sr.data || []); setStream(st.data || []);
        // The F&I queue is loaded HERE, not inside the panel: the tab badge has
        // to be live whether or not the collapsed panel is mounted. A collapsed
        // queue reading "(0)" while leads wait is the dead end this part removes.
        try {
            const [fa, fw] = await Promise.all([
                fiLeadsAPI.financing(organizationId),
                fiLeadsAPI.warranty(organizationId),
            ]);
            setFiApps(fa.data?.applications || []);
            setFiInterest(fw.data?.interest || []);
            setFiFailed(false);
        } catch { setFiFailed(true); }
        try {
            const rb = await rebateAlertsAPI.list(organizationId, locationIds);
            setRebateCount(rb.data?.count || 0);
        } catch { setRebateCount(0); }
        try {
            const st2 = await vehicleSettingsAPI.get(organizationId);
            setPayEst(st2.data?.payment_estimate_enabled === true);
        } catch { /* non-manager roles cannot read settings; control stays hidden */ }
    }, [organizationId, days, locationIds]);
    useEffect(() => { load(); const id = setInterval(load, 10000); return () => clearInterval(id); }, [load]);

    // The F&I / Insurance department tile lives in the shell's status strip and
    // opens this panel's existing F&I queue — no second panel.
    useEffect(() => {
        if (focusDept?.id === 'finance_insurance') pickTab('fileads');
    }, [focusDept, pickTab]);

    const openConvo = async (cid) => { const r = await vehicleActivityAPI.conversation(cid); setConvo(r.data); };

    const f = sum?.funnel || {};
    const needTD = drives.filter((d) => d.status === 'requested');
    // Only OPEN inquiries are outstanding work — the same filter needTD applies,
    // so closed/resolved rows stop accumulating in anything that counts work.
    const openSales = sales.filter((q) => (q.status || 'open') === 'open');
    useEffect(() => {
        setOpenFILeads([...fiApps, ...fiInterest].filter((l) => (l.lead_status || 'open') === 'open').length);
    }, [fiApps, fiInterest]);
    const stat = (icon, label, value, pct, tid) => (
        <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-700 dark:bg-slate-800/60 dark:shadow-none" data-testid={tid}>
            <div className="text-amber-600 dark:text-amber-500">{icon}</div>
            <div className="text-3xl font-extrabold text-slate-900 dark:text-white mt-1">{value ?? 0}</div>
            <div className="text-xs text-slate-600 dark:text-slate-400">{label}{pct != null ? ` · ${pct}%` : ''}</div>
        </div>
    );

    return (
        <section className="space-y-4" data-testid="cc-automotive-funnel-panel">
            <div className="flex items-center justify-between gap-3 flex-wrap">
                <h2 className="text-lg font-semibold text-slate-800 dark:text-slate-200 flex items-center gap-2">
                    <Gift className="w-4 h-4 text-amber-500" />{t('commandCenter.automotive.funnelTitle', 'Sales Funnel')}
                </h2>
                <Select value={String(days)} onValueChange={(v) => setDays(Number(v))}>
                    <SelectTrigger className="w-36" data-testid="cc-range"><SelectValue /></SelectTrigger>
                    <SelectContent>
                        <SelectItem value="7">{t('commandCenter.automotive.last7', 'Last 7 days')}</SelectItem>
                        <SelectItem value="30">{t('commandCenter.automotive.last30', 'Last 30 days')}</SelectItem>
                        <SelectItem value="90">{t('commandCenter.automotive.last90', 'Last 90 days')}</SelectItem>
                    </SelectContent>
                </Select>
            </div>

            {/* PHASE D — Rebate Alert: prominent high/red attention state (matches
                the shared Command Center Needs-Attention severity model). */}
            {rebateCount > 0 && (
                <button type="button" onClick={() => pickTabAndReveal('rebate')} data-testid="cc-rebate-banner"
                    className="w-full text-left rounded-2xl border border-red-500/60 bg-red-500/10 p-4 flex items-center gap-4 hover:bg-red-500/15 transition-colors">
                    <div className="h-11 w-11 rounded-xl bg-red-500/20 flex items-center justify-center shrink-0">
                        <Tag className="w-6 h-6 text-red-400" />
                    </div>
                    <div className="flex-1 min-w-0">
                        <div className="text-[11px] font-bold uppercase tracking-widest text-red-400">{t('rebateAlert.title')}</div>
                        <div className="text-2xl font-extrabold text-slate-900 dark:text-white leading-tight" data-testid="cc-rebate-count">
                            {t('rebateAlert.vehiclesCount', { count: rebateCount })}
                        </div>
                        <div className="text-xs text-slate-600 dark:text-slate-300 mt-0.5">{t('rebateAlert.banner')}</div>
                    </div>
                    <ArrowRight className="w-5 h-5 text-red-400 shrink-0" />
                </button>
            )}

            {/* Org-wide buyer-page default. Always visible, never behind a tab
                or inside a vehicle dialog — it governs every vehicle page. */}
            {payEst !== null && (
                <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm flex items-center justify-between gap-4 dark:border-slate-700 dark:bg-slate-800/60 dark:shadow-none"
                    data-testid="cc-payment-estimate-card">
                    <div className="min-w-0">
                        <div className="text-amber-600 dark:text-amber-500 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide">
                            <BadgePercent className="w-4 h-4" />{t('ccPayEst.title', 'Estimated monthly payment')}
                        </div>
                        <div className="text-sm text-slate-800 dark:text-slate-200 mt-1 font-medium">
                            {t('ccPayEst.label', 'Show estimated monthly payment on all vehicle pages')}
                        </div>
                        <div className="text-[11px] text-slate-600 dark:text-slate-400 mt-0.5">
                            {t('ccPayEst.hint', 'Applies to every vehicle in this dealership. A single vehicle can override it from its Manage panel.')}
                        </div>
                    </div>
                    <Switch checked={!!payEst} disabled={payEstBusy} data-testid="cc-payment-estimate-switch"
                        onCheckedChange={async (next) => {
                            setPayEstBusy(true);
                            const prev = payEst;
                            setPayEst(next);            // optimistic
                            try {
                                await vehicleSettingsAPI.update(organizationId, { payment_estimate_enabled: next });
                                toast.success(next ? t('ccPayEst.on', 'Estimated payment is now showing on vehicle pages')
                                    : t('ccPayEst.off', 'Estimated payment is now hidden on vehicle pages'));
                            } catch (e) {
                                setPayEst(prev);        // roll back so the switch never lies
                                toast.error(e.response?.data?.detail || t('common.error', 'Something went wrong'));
                            } finally { setPayEstBusy(false); }
                        }} />
                </div>
            )}

            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3" data-testid="cc-funnel">
                {stat(<Eye className="w-5 h-5" />, t('commandCenter.automotive.visits', 'Visits'), f.visits, null, 'cc-stat-visits')}
                {stat(<MessageSquare className="w-5 h-5" />, t('commandCenter.automotive.questions', 'Questions'), f.questions, f.questions_pct, 'cc-stat-questions')}
                {stat(<CalendarClock className="w-5 h-5" />, t('commandCenter.automotive.testDrives', 'Test drives'), f.test_drives, f.test_drives_pct, 'cc-stat-testdrives')}
                {stat(<Heart className="w-5 h-5" />, t('commandCenter.automotive.saves', 'Saves'), f.saves, f.saves_pct, 'cc-stat-saves')}
                {stat(<Gift className="w-5 h-5" />, t('commandCenter.automotive.offers', 'Offers'), f.offers_claimed, f.offers_pct, 'cc-stat-offers')}
            </div>

            {/* Vehicle Shares — 'Send to someone' metric (derived from activity_events) */}
            <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm flex items-center justify-between gap-4 dark:border-slate-700 dark:bg-slate-800/60 dark:shadow-none" data-testid="cc-stat-shares">
                <div>
                    <div className="text-amber-600 dark:text-amber-500 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide">
                        <Share2 className="w-4 h-4" />{t('commandCenter.automotive.vehicleShares', 'Vehicle Shares')}
                    </div>
                    <div className="text-3xl font-extrabold text-slate-900 dark:text-white mt-1" data-testid="cc-shares-count">{sum?.shares?.shares ?? 0}</div>
                </div>
                <div className="flex gap-5 text-right">
                    <div>
                        <div className="text-xl font-bold text-slate-900 dark:text-white" data-testid="cc-shares-opens">{sum?.shares?.opens ?? 0}</div>
                        <div className="text-[11px] text-slate-600 dark:text-slate-400">{t('commandCenter.automotive.linkOpens', 'Link opens')}</div>
                    </div>
                    <div>
                        <div className="text-xl font-bold text-slate-900 dark:text-white" data-testid="cc-shares-views">{sum?.shares?.views ?? 0}</div>
                        <div className="text-[11px] text-slate-600 dark:text-slate-400">{t('commandCenter.automotive.viewsFromShares', 'Vehicle views')}</div>
                    </div>
                </div>
            </div>

            <div ref={tabsRef} data-testid="cc-tabs-anchor" className="scroll-mt-4">
            <Tabs value={tab} onValueChange={pickTab}>
                <TabsList variant="pill">
                    <TabsTrigger variant="pill" value="rebate" data-testid="cc-tab-rebate">{t('rebateAlert.tabTitle', 'Promotions')}<TabsCount value={rebateCount} /></TabsTrigger>
                    <TabsTrigger variant="pill" value="activity" data-testid="cc-tab-activity">{t('commandCenter.automotive.activityTab', 'Activity')}<TabsCount value={stream.length} /></TabsTrigger>
                    <TabsTrigger variant="pill" value="alerts" data-testid="cc-tab-alerts">{t('autoAlerts.tabTitle', 'Sales Alerts')}<TabsCount value={needTD.length + openSales.length} /></TabsTrigger>
                    <TabsTrigger variant="pill" value="notify" data-testid="cc-tab-notify">{t('notify.tabTitle', 'Notification Routing')}</TabsTrigger>
                    <TabsTrigger variant="pill" value="fileads" data-testid="cc-tab-fileads">{t('fiLeads.tabTitle', 'F&I leads')}<TabsCount value={openFILeads} /></TabsTrigger>
                    <TabsTrigger variant="pill" value="buyer" data-testid="cc-tab-buyer">{t('ccBuyerExp.tabTitle', 'Buyer experience')}</TabsTrigger>
                </TabsList>

                <TabsContent value="notify" className="mt-4" data-testid="cc-notify">
                    {/* The combined picture goes FIRST — it is the only place that shows
                        the assigned person and the broadcast list together, which is what
                        was missing. The per-event routing panel stays underneath. */}
                    <CollapsibleList title={t('vehNotify.title', 'Who gets notified')} testid="cc-vehicle-notify-collapse">
                        <VehicleNotificationsPanel organizationId={organizationId} />
                    </CollapsibleList>
                    <div className="mt-4">
                        <CollapsibleList title={t('notify.tabTitle', 'Notification Routing')} testid="cc-notify-collapse">
                            <NotifyRoutingPanel organizationId={organizationId} recordKind="vehicle" vertical="automotive" />
                        </CollapsibleList>
                    </div>
                </TabsContent>

                <TabsContent value="fileads" className="mt-4" data-testid="cc-fileads">
                    <CollapsibleList title={t('fiLeads.tabTitle', 'F&I leads')} count={openFILeads} testid="cc-fileads-collapse" defaultOpen>
                        <CCFILeadsPanel apps={fiApps} interest={fiInterest} failed={fiFailed} openCount={openFILeads} onChanged={load} />
                    </CollapsibleList>
                </TabsContent>

                <TabsContent value="buyer" className="mt-4" data-testid="cc-buyer">
                    <CollapsibleList title={t('ccBuyerExp.tabTitle', 'Buyer experience')} testid="cc-buyer-exp-collapse">
                        <CCBuyerExperiencePanel organizationId={organizationId} />
                    </CollapsibleList>
                </TabsContent>

                <TabsContent value="rebate" className="mt-4" data-testid="cc-rebate">
                    <CollapsibleList title={t('rebateAlert.tabTitle', 'Promotions')} count={rebateCount} testid="cc-rebate-collapse" defaultOpen>
                        <RebateAlerts organizationId={organizationId} locationIds={locationIds} onChanged={load} />
                    </CollapsibleList>
                    {/* The queue above can only ever fill if a schedule exists. */}
                    <div className="mt-4">
                        <CollapsibleList title={t('specialSched.title', 'Aging incentive schedules')} testid="cc-sched-collapse">
                            <CCSpecialSchedules organizationId={organizationId} />
                        </CollapsibleList>
                    </div>
                </TabsContent>

                <TabsContent value="alerts" className="mt-4" data-testid="cc-alerts">
                    <CollapsibleList title={t('autoAlerts.tabTitle', 'Sales Alerts')} testid="cc-alerts-collapse">
                        <AutoSalesAlerts organizationId={organizationId} scope="command_center" />
                    </CollapsibleList>
                </TabsContent>

                <TabsContent value="activity" className="mt-4 space-y-1.5" data-testid="cc-activity">
                    <CollapsibleList title={t('commandCenter.automotive.activityTab', 'Activity')} count={stream.length} testid="cc-activity-collapse">
                    <div className="space-y-1.5">
                    {stream.map((e) => (
                        <div key={e.id} className="flex items-center gap-3 text-sm py-1.5 border-b border-slate-200 dark:border-slate-800" data-testid="cc-event-row">
                            <Clock className="w-3.5 h-3.5 text-slate-500 dark:text-slate-400" />
                            <span className="text-slate-700 dark:text-slate-300 w-44">{EVENT_LABELS[e.event_type] || e.event_type}</span>
                            <span className="text-slate-600 dark:text-slate-400 flex-1">{e.subject?.stock_number ? `#${e.subject.stock_number}` : ''} {e.actor_name || ''}</span>
                            <span className="text-slate-600 dark:text-slate-400 text-xs">{fmt(e.created_at)}</span>
                        </div>
                    ))}
                    </div>
                    </CollapsibleList>
                </TabsContent>
            </Tabs>
            </div>

            {/* Conversation drill-down */}
            <Dialog open={!!convo} onOpenChange={(o) => !o && setConvo(null)}>
                <DialogContent className="sm:max-w-md max-h-[80vh] overflow-y-auto bg-slate-950 border-slate-800 text-slate-100" data-testid="cc-conversation">
                    <DialogHeader><DialogTitle className="text-white">{convo?.request && `${convo.request.year} ${convo.request.make} ${convo.request.model} · #${convo.request.stock_number}`}</DialogTitle></DialogHeader>
                    <div className="space-y-2">
                        {(convo?.messages || []).map((m) => (
                            <div key={m.id} className={`p-2.5 rounded-lg text-sm ${m.sender_role === 'sales' ? 'bg-amber-500/10 ml-6' : 'bg-slate-800 mr-6'}`}>
                                <p className="text-[11px] text-slate-400 mb-0.5">{m.sender_name}{m.channel === 'sms' ? ` · SMS${m.sms_status ? ' · ' + m.sms_status : ''}` : ''} · {fmt(m.created_at)}</p>
                                <p className="text-slate-100">{m.body}</p>
                            </div>
                        ))}
                    </div>
                </DialogContent>
            </Dialog>
        </section>
    );
}
